import { useQuery } from '@tanstack/react-query'

import type { PickOption } from '../components/ui/searchPick'
import { holdsTick } from '../pages/inventory/inventoryTab'
import { computeTotals, lineAmountOf, type LineInput, type Totals, type VatType } from './money'
import type { User } from './types'

export const PUBLIC_PAGE_PATH = '/oreas-stock'
export const EMBED_PATH = '/embed/oreas-stock'
export const FEED_PATH = '/api/public/oreas/stock'
export const REQUEST_PATH = '/api/public/oreas/requests'
const QTY_MAX = 100000

export interface OreasProduct {
  id: string
  code: string
  certified_values: string[]
  price_mnt: string | null
  stock_qty: number
  incoming_qty: number
  material: string | null
  deposit_type: string | null
  certificate_url: string | null
  is_active: boolean
  updated_at: string
}

export interface OreasCatalogue {
  items: OreasProduct[]
  stock_total: number
  incoming_total: number
}

export interface OreasPayload {
  code: string
  certified_values: string[]
  price_mnt: string | null
  stock_qty: number
  incoming_qty: number
  material: string | null
  deposit_type: string | null
  certificate_url: string | null
  is_active: boolean
}

export interface InStockRow {
  code: string
  certified_values: string[]
  material: string | null
  quantity: number
  price_mnt: string | null
  certificate_url: string | null
}

export interface IncomingRow {
  code: string
  certified_values: string[]
  material: string | null
  deposit_type: string | null
  quantity: number
  certificate_url: string | null
}

export interface PublicStock {
  in_stock: InStockRow[]
  incoming: IncomingRow[]
}

/** Who sees add and edit; the server's own rule is `CanWrite` in the oreas router. */
export function canWriteOreas(user: User | undefined | null): boolean {
  return holdsTick(user, 'oreas')
}

export type PublicStockMode = 'page' | 'embed'

export function publicStockMode(pathname: string): PublicStockMode | null {
  const path = pathname.replace(/\/+$/, '')
  if (path === PUBLIC_PAGE_PATH) return 'page'
  if (path === EMBED_PATH) return 'embed'
  return null
}

export function joinCertified(values: readonly string[]): string {
  return values.join(' · ')
}

/** Rounded only here: the stored price keeps its full precision. Null means not set, never 0. */
export function formatWholeMnt(price: string | null | undefined): string | null {
  if (price == null || price === '') return null
  const numeric = Number(price)
  if (!Number.isFinite(numeric)) return null
  return Math.round(numeric).toLocaleString('en-US')
}

/** A link is only ever rendered as an href when it is a web address. */
export function safeLink(url: string | null | undefined): string | null {
  return url && /^https?:\/\//i.test(url) ? url : null
}

export type ThemeParam = 'light' | 'dark' | 'system'

export function themeParam(value: string | null): ThemeParam {
  return value === 'light' || value === 'dark' ? value : 'system'
}

export function languageParam(value: string | null, fallback: string): string {
  return value === 'en' || value === 'mn' || value === 'ru' ? value : fallback
}

export type SectionFilter = 'all' | 'in_stock' | 'incoming'

export type AnalyteMatch = 'all' | 'any'

/** What both tables narrow by, apart from the search and the quantities. */
export interface ProductPicks {
  material: string
  depositType: string
  analytes: readonly string[]
  analyteMatch: AnalyteMatch
}

export const NO_PICKS: ProductPicks = { material: '', depositType: '', analytes: [], analyteMatch: 'all' }

export interface StockFilter extends ProductPicks {
  search: string
  section: SectionFilter
  least: string
  most: string
}

export const NO_FILTER: StockFilter = { ...NO_PICKS, search: '', section: 'all', least: '', most: '' }

const SHARED_CODE_PREFIX = /^oreas[\s_]*/

/** Every code starts with OREAS, so that word tells rows apart only once it is dropped from both sides. */
function codeMatches(code: string, needle: string): boolean {
  return code.toLowerCase().replace(SHARED_CODE_PREFIX, '').includes(needle.replace(SHARED_CODE_PREFIX, ''))
}

/** The element, from its start so "u" does not find every Au and Cu; a value that fits no columns is searched whole. */
function certifiedMatches(values: readonly string[], needle: string): boolean {
  return values.some((value) => {
    const row = certifiedRow(value)
    return row.raw !== null ? row.raw.toLowerCase().includes(needle) : row.element.toLowerCase().startsWith(needle)
  })
}

function matchesSearch(
  row: { code: string; material: string | null; certified_values: readonly string[] },
  search: string,
): boolean {
  const needle = search.trim().toLowerCase()
  return (
    !needle ||
    codeMatches(row.code, needle) ||
    (row.material ?? '').toLowerCase().includes(needle) ||
    certifiedMatches(row.certified_values, needle)
  )
}

function holdsAnalytes(values: readonly string[], picks: Pick<ProductPicks, 'analytes' | 'analyteMatch'>): boolean {
  const held = new Set(values.map(certifiedAnalyte))
  const holds = (analyte: string) => held.has(analyte)
  return picks.analytes.length === 0 || (picks.analyteMatch === 'any' ? picks.analytes.some(holds) : picks.analytes.every(holds))
}

/** One rule for both tables; a value `certifiedAnalyte` cannot read is never picked, and the search still finds it. */
export function matchesPicks(
  row: { material: string | null; deposit_type?: string | null; certified_values: readonly string[] },
  picks: ProductPicks,
): boolean {
  return (
    (!picks.material || row.material === picks.material) &&
    (!picks.depositType || row.deposit_type === picks.depositType) &&
    holdsAnalytes(row.certified_values, picks)
  )
}

function matches(
  row: { code: string; material: string | null; deposit_type?: string | null; certified_values: readonly string[]; quantity: number },
  filter: StockFilter,
): boolean {
  return matchesSearch(row, filter.search) && matchesPicks(row, filter) && withinQuantity(row.quantity, filter.least, filter.most)
}

export function filterStock(stock: PublicStock, filter: StockFilter): PublicStock {
  return {
    in_stock:
      filter.section === 'incoming' || filter.depositType
        ? []
        : stock.in_stock.filter((row) => matches(row, filter)),
    incoming: filter.section === 'in_stock' ? [] : stock.incoming.filter((row) => matches(row, filter)),
  }
}

export function distinct(values: readonly (string | null)[]): string[] {
  return [...new Set(values.filter((value): value is string => !!value))].sort((a, b) => a.localeCompare(b))
}

export function materialOptions(stock: PublicStock): string[] {
  return distinct([...stock.in_stock, ...stock.incoming].map((row) => row.material))
}

export function depositTypeOptions(stock: PublicStock): string[] {
  return distinct(stock.incoming.map((row) => row.deposit_type))
}

export type StockSection = Exclude<SectionFilter, 'all'>

export const FIRST_VIEW: StockFilter = { ...NO_FILTER, section: 'in_stock' }

/** Keeps what was typed; a material, deposit type or analyte chosen on one list may not exist on the other. */
export function switchSection(filter: StockFilter, section: StockSection): StockFilter {
  return { ...NO_FILTER, search: filter.search, least: filter.least, most: filter.most, section }
}

export function otherSection(section: StockSection): StockSection {
  return section === 'in_stock' ? 'incoming' : 'in_stock'
}

export function isNarrowed(filter: StockFilter): boolean {
  return (
    filter.search.trim() !== '' ||
    filter.material !== '' ||
    filter.depositType !== '' ||
    filter.analytes.length > 0 ||
    filter.least.trim() !== '' ||
    filter.most.trim() !== ''
  )
}

/** Each count is what its tab shows on opening, so the other tab is counted as switching would leave it. */
export function sectionCounts(stock: PublicStock, filter: StockFilter, active: StockSection): Record<StockSection, number> {
  const onActive = filterStock(stock, { ...filter, section: active })
  const onOther = filterStock(stock, switchSection(filter, otherSection(active)))
  return active === 'in_stock'
    ? { in_stock: onActive.in_stock.length, incoming: onOther.incoming.length }
    : { in_stock: onOther.in_stock.length, incoming: onActive.incoming.length }
}

export function sectionMaterials(stock: PublicStock, section: StockSection): string[] {
  return materialOptions(section === 'in_stock' ? { ...stock, incoming: [] } : { ...stock, in_stock: [] })
}

/** Offers what no other pick shares too, because the switch to `any` only exists from two picks and must stay reachable. */
export function analyteChoices(rows: readonly { certified_values: readonly string[] }[], picked: readonly string[]): string[] {
  return distinct(rows.flatMap((row) => row.certified_values.map(certifiedAnalyte))).filter((analyte) => !picked.includes(analyte))
}

export function addAnalyte<Picks extends ProductPicks>(picks: Picks, analyte: string): Picks {
  return picks.analytes.includes(analyte) ? picks : { ...picks, analytes: [...picks.analytes, analyte] }
}

/** The switch only exists from two picks, so below that it goes back to where it starts. */
export function dropAnalyte<Picks extends ProductPicks>(picks: Picks, analyte: string): Picks {
  const analytes = picks.analytes.filter((picked) => picked !== analyte)
  return { ...picks, analytes, analyteMatch: analytes.length < 2 ? 'all' : picks.analyteMatch }
}

/** Blank first so a list can be emptied, and the current pick kept even when the rows no longer hold it. */
export function pickOptions(allLabel: string, values: readonly string[], current: string): PickOption[] {
  return [
    { value: '', label: allLabel, group: '' },
    ...distinct([...values, current]).map((value) => ({ value, label: value, group: '' })),
  ]
}

/** The stored spelling of what was typed, exact before any other case; null is a truly new element. */
export function storedSpelling(typed: string, known: readonly string[]): string | null {
  const wanted = typed.trim()
  if (!wanted) return null
  if (known.includes(wanted)) return wanted
  return known.find((element) => element.toLowerCase() === wanted.toLowerCase()) ?? null
}

export function embedSnippet(origin: string): string {
  return `<iframe src="${origin}${EMBED_PATH}" title="OREAS stock" style="width:100%;height:900px;border:0" loading="lazy"></iframe>`
}

export function feedAddress(origin: string): string {
  return `${origin}${FEED_PATH}`
}

export const CERTIFIED_UNITS = ['ppm', 'ppb', 'wt.%'] as const
export const MAX_CERTIFIED_ROWS = 20

export interface CertifiedRow {
  element: string
  below: boolean
  amount: string
  unit: string
  raw: string | null
  source: string | null
}

export const BLANK_CERTIFIED_ROW: CertifiedRow = {
  element: '',
  below: false,
  amount: '',
  unit: CERTIFIED_UNITS[0],
  raw: null,
  source: null,
}

const CERTIFIED_VALUE = /^(.+?)\s*(<?)\s*(\d[\d.,]*)\s*(ppm|ppb|wt\.%)$/

/** A string that does not fit the columns is kept whole in `raw`, never dropped. */
export function certifiedRow(text: string): CertifiedRow {
  const found = CERTIFIED_VALUE.exec(text)
  if (!found) return { ...BLANK_CERTIFIED_ROW, raw: text, source: text }
  return { element: found[1], below: found[2] === '<', amount: found[3], unit: found[4], raw: null, source: text }
}

/** An untouched row gives back its exact source, because the sheets space these strings in several ways. */
export function certifiedText(row: CertifiedRow): string {
  if (row.raw !== null) return row.raw.trim()
  if (row.source !== null) {
    const was = certifiedRow(row.source)
    if (was.element === row.element && was.below === row.below && was.amount === row.amount && was.unit === row.unit) {
      return row.source
    }
  }
  return `${row.element.trim()} ${row.below ? '<' : ''}${row.amount.trim()}${row.unit}`
}

const PPB_IN_UNIT: Readonly<Record<string, number>> = { ppb: 1, ppm: 1_000, 'wt.%': 10_000_000 }

/** In ppb so units compare; below detection counts at its number; null is a value that cannot be read, such as a comma that could mean either thing. */
export function certifiedAmount(text: string | undefined): number | null {
  if (text === undefined) return null
  const row = certifiedRow(text)
  if (row.raw !== null || !/^\d+(\.\d+)?$/.test(row.amount)) return null
  return Number(row.amount) * PPB_IN_UNIT[row.unit]
}

/** `Au<1ppb` is Au, not a choice of its own. */
export function certifiedAnalyte(text: string): string | null {
  const row = certifiedRow(text)
  return row.raw === null ? row.element : null
}

/** Where a value may wrap: after its analyte, never inside its figure. The two parts join back to exactly what is stored. */
export function certifiedParts(text: string): { analyte: string; figure: string } {
  const row = certifiedRow(text)
  if (row.raw !== null) return { analyte: text, figure: '' }
  const figureAt = /^\s*/.exec(text.slice(row.element.length))?.[0].length ?? 0
  const split = row.element.length + figureAt
  return { analyte: text.slice(0, split), figure: text.slice(split) }
}

/** Each pick takes its own column in pick order, blank where a product lacks it, so sorting a column compares like with like. */
export function valuesInColumns(values: readonly string[], analytes: readonly string[] = []): string[] {
  const held = analytes.map((analyte) => values.findIndex((value) => certifiedAnalyte(value) === analyte))
  return [...held.map((at) => (at < 0 ? '' : values[at])), ...values.filter((_, at) => !held.includes(at))]
}

const FEWEST_VALUE_COLUMNS = 3

/** Never fewer than three so the table holds still, and more when a product stores more, because the form allows it. */
export function valueColumnCount(
  rows: readonly { certified_values: readonly string[] }[],
  analytes: readonly string[] = [],
): number {
  return Math.max(FEWEST_VALUE_COLUMNS, ...rows.map((row) => valuesInColumns(row.certified_values, analytes).length))
}

/** Digits only, so a range box never holds something it would have to ignore. */
export function quantityText(typed: string): string {
  return typed.replace(/\D/g, '')
}

/** A blank side sets no limit, and 0 is a real limit. */
export function withinQuantity(quantity: number, least: string, most: string): boolean {
  const low = least.trim()
  const high = most.trim()
  return (low === '' || quantity >= Number(low)) && (high === '' || quantity <= Number(high))
}

export type SortDir = 'asc' | 'desc'
export type ValueColumn = `value${number}`
export type StockSortKey = 'product' | 'material' | 'deposit' | 'quantity' | 'price' | 'stock' | 'incoming' | ValueColumn

export interface StockSort {
  key: StockSortKey
  dir: SortDir
}

export const FIRST_SORT: StockSort = { key: 'product', dir: 'asc' }

export function valueColumnKeys(count: number): ValueColumn[] {
  return Array.from({ length: count }, (_, index): ValueColumn => `value${index}`)
}

export function nextSort(sort: StockSort, key: StockSortKey): StockSort {
  return { key, dir: sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc' }
}

export function ariaSortOf(sort: StockSort, key: StockSortKey): 'ascending' | 'descending' | 'none' {
  if (sort.key !== key) return 'none'
  return sort.dir === 'asc' ? 'ascending' : 'descending'
}

export function sectionSortKeys(section: StockSection, valueCount: number): StockSortKey[] {
  const values = valueColumnKeys(valueCount)
  return section === 'in_stock'
    ? ['product', ...values, 'material', 'quantity', 'price']
    : ['product', ...values, 'material', 'deposit', 'quantity']
}

/** A column the open tab does not have cannot be what it is sorted by. */
export function sortForKeys(sort: StockSort, keys: readonly StockSortKey[]): StockSort {
  return keys.includes(sort.key) ? sort : FIRST_SORT
}

interface SortableRow {
  code: string
  certified_values: readonly string[]
  material: string | null
  deposit_type?: string | null
  quantity?: number
  price_mnt?: string | null
  stock_qty?: number
  incoming_qty?: number
}

function priceAmount(price: string | null | undefined): number | null {
  if (price == null || price === '') return null
  const numeric = Number(price)
  return Number.isFinite(numeric) ? numeric : null
}

function sortValue(row: SortableRow, key: StockSortKey, analytes: readonly string[]): string | number | null {
  switch (key) {
    case 'product':
      return row.code
    case 'material':
      return row.material?.trim() || null
    case 'deposit':
      return row.deposit_type?.trim() || null
    case 'quantity':
      return row.quantity ?? null
    case 'stock':
      return row.stock_qty ?? null
    case 'incoming':
      return row.incoming_qty ?? null
    case 'price':
      return priceAmount(row.price_mnt)
    default:
      return certifiedAmount(valuesInColumns(row.certified_values, analytes)[Number(key.slice('value'.length))])
  }
}

const TEXT_ORDER = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

function compareKnown(a: string | number, b: string | number): number {
  return typeof a === 'number' && typeof b === 'number' ? a - b : TEXT_ORDER.compare(String(a), String(b))
}

/** Nothing to compare goes last in both directions, so turning the sort never leads with blanks; ties keep code order. */
export function sortStockRows<Row extends SortableRow>(rows: readonly Row[], sort: StockSort, analytes: readonly string[] = []): Row[] {
  const direction = sort.dir === 'asc' ? 1 : -1
  return rows
    .map((row) => ({ row, value: sortValue(row, sort.key, analytes) }))
    .sort((a, b) => {
      const known =
        a.value === null || b.value === null
          ? Number(a.value === null) - Number(b.value === null)
          : direction * compareKnown(a.value, b.value)
      return known || TEXT_ORDER.compare(a.row.code, b.row.code)
    })
    .map(({ row }) => row)
}

export function sortStock(stock: PublicStock, sort: StockSort, analytes: readonly string[]): PublicStock {
  return { in_stock: sortStockRows(stock.in_stock, sort, analytes), incoming: sortStockRows(stock.incoming, sort, analytes) }
}

function certifiedRowIsBlank(row: CertifiedRow): boolean {
  return row.raw !== null ? !row.raw.trim() : !row.element.trim() && !row.amount.trim()
}

function certifiedRowIsWhole(row: CertifiedRow): boolean {
  return row.raw !== null || (!!row.element.trim() && /^\d[\d.,]*$/.test(row.amount.trim()))
}

export interface OreasValue {
  label: string
  products: number
}

export type OreasValueKind = 'material' | 'deposit_type'
export type OreasValues = Record<OreasValueKind, OreasValue[]>

/** Spellings of one value that differ only in capitals, the most used first. */
export function capitalsOnlyGroups(values: readonly OreasValue[]): OreasValue[][] {
  const groups = new Map<string, OreasValue[]>()
  for (const value of values) {
    const key = value.label.toLowerCase()
    groups.set(key, [...(groups.get(key) ?? []), value])
  }
  return [...groups.values()]
    .filter((group) => group.length > 1)
    .map((group) => [...group].sort((a, b) => b.products - a.products || a.label.localeCompare(b.label)))
}

/** What a dropdown offers; the value a product holds stays offered even when the list could not load. */
export function valueChoices(values: readonly OreasValue[], current: string): string[] {
  const labels = values.map((value) => value.label)
  return current && !labels.includes(current) ? [current, ...labels] : labels
}

export interface OreasForm {
  code: string
  certified: CertifiedRow[]
  price: string
  stock: string
  incoming: string
  material: string
  depositType: string
  link: string
  active: boolean
}

export function formFromProduct(product: OreasProduct | null): OreasForm {
  return {
    code: product?.code ?? '',
    certified: (product?.certified_values ?? []).map(certifiedRow),
    price: product?.price_mnt ?? '',
    stock: String(product?.stock_qty ?? 0),
    incoming: String(product?.incoming_qty ?? 0),
    material: product?.material ?? '',
    depositType: product?.deposit_type ?? '',
    link: product?.certificate_url ?? '',
    active: product?.is_active ?? true,
  }
}

export type OreasFormProblem = 'code' | 'price' | 'quantity' | 'link' | 'content'
export type OreasFormErrors = Partial<
  Record<'code' | 'certified' | 'price' | 'stock' | 'incoming' | 'link', OreasFormProblem>
>

function validQuantity(raw: string): boolean {
  const text = raw.trim()
  return /^\d+$/.test(text) && Number(text) <= QTY_MAX
}

/** Names what is wrong with each field; an empty object means the form can be sent. */
export function validateForm(form: OreasForm): OreasFormErrors {
  const errors: OreasFormErrors = {}
  if (!form.code.trim()) errors.code = 'code'
  if (form.certified.some((row) => !certifiedRowIsBlank(row) && !certifiedRowIsWhole(row))) errors.certified = 'content'
  const price = form.price.trim()
  if (price && (!/^\d+(\.\d+)?$/.test(price))) errors.price = 'price'
  if (!validQuantity(form.stock)) errors.stock = 'quantity'
  if (!validQuantity(form.incoming)) errors.incoming = 'quantity'
  const link = form.link.trim()
  if (link && (!safeLink(link) || /\s/.test(link))) errors.link = 'link'
  return errors
}

export function payloadFromForm(form: OreasForm): OreasPayload {
  return {
    code: form.code.trim(),
    certified_values: form.certified.filter((row) => !certifiedRowIsBlank(row)).map(certifiedText),
    price_mnt: form.price.trim() || null,
    stock_qty: Number(form.stock.trim()),
    incoming_qty: Number(form.incoming.trim()),
    material: form.material.trim() || null,
    deposit_type: form.depositType.trim() || null,
    certificate_url: form.link.trim() || null,
    is_active: form.active,
  }
}

export interface CatalogueRanges {
  stockLeast: string
  stockMost: string
  incomingLeast: string
  incomingMost: string
}

export const NO_RANGES: CatalogueRanges = { stockLeast: '', stockMost: '', incomingLeast: '', incomingMost: '' }

export function filterCatalogue(
  items: readonly OreasProduct[],
  search: string,
  ranges: CatalogueRanges = NO_RANGES,
  picks: ProductPicks = NO_PICKS,
): OreasProduct[] {
  return items.filter(
    (item) =>
      matchesSearch(item, search) &&
      matchesPicks(item, picks) &&
      withinQuantity(item.stock_qty, ranges.stockLeast, ranges.stockMost) &&
      withinQuantity(item.incoming_qty, ranges.incomingLeast, ranges.incomingMost),
  )
}

export function catalogueNarrowed(search: string, ranges: CatalogueRanges, picks: ProductPicks = NO_PICKS): boolean {
  return (
    search !== '' ||
    Object.values(ranges).some((typed) => typed !== '') ||
    picks.material !== '' ||
    picks.depositType !== '' ||
    picks.analytes.length > 0
  )
}

export function catalogueSortKeys(valueCount: number): StockSortKey[] {
  return ['product', ...valueColumnKeys(valueCount), 'price', 'stock', 'incoming', 'material', 'deposit']
}

export interface CustomerForm {
  name: string
  company: string
  phone: string
  email: string
  registration: string
  address: string
  note: string
  website: string
}

export const NO_CUSTOMER: CustomerForm = {
  name: '',
  company: '',
  phone: '',
  email: '',
  registration: '',
  address: '',
  note: '',
  website: '',
}

export type CustomerField = 'name' | 'company' | 'phone' | 'email'
export type CustomerErrors = Partial<Record<CustomerField, CustomerField>>

/** Names each required field that is missing or malformed; the server checks the same rules again. */
export function validateCustomer(form: CustomerForm): CustomerErrors {
  const errors: CustomerErrors = {}
  if (!form.name.trim()) errors.name = 'name'
  if (!form.company.trim()) errors.company = 'company'
  if (!/^\+?[0-9][0-9 ()-]{5,29}$/.test(form.phone.trim())) errors.phone = 'phone'
  if (!/^[^@\s<>"(),:;[\]\\]+@[^@\s<>"(),:;[\]\\]+\.[^@\s<>"(),:;[\]\\]+$/.test(form.email.trim())) errors.email = 'email'
  return errors
}

export type NeedCart = Readonly<Record<string, string>>

export function toggleNeed(cart: NeedCart, code: string): NeedCart {
  if (code in cart) {
    const rest = { ...cart }
    delete rest[code]
    return rest
  }
  return { ...cart, [code]: '1' }
}

export type NeedProblem = 'quantity' | 'tooMany' | 'gone'

/** A ticked product must still be on the page, and cannot ask for more than the page shows. */
export function needProblems(cart: NeedCart, rows: readonly InStockRow[]): Record<string, NeedProblem> {
  const shown = new Map(rows.map((row) => [row.code, row.quantity]))
  const problems: Record<string, NeedProblem> = {}
  for (const [code, raw] of Object.entries(cart)) {
    const available = shown.get(code)
    const text = raw.trim()
    if (available === undefined) problems[code] = 'gone'
    else if (!/^\d+$/.test(text) || Number(text) < 1) problems[code] = 'quantity'
    else if (Number(text) > available) problems[code] = 'tooMany'
  }
  return problems
}

export interface CartLine {
  code: string
  quantity: string
  available: number | null
  problem: NeedProblem | null
}

export function cartLines(
  cart: NeedCart,
  rows: readonly InStockRow[],
  problems: Readonly<Record<string, NeedProblem>>,
): CartLine[] {
  const shown = new Map(rows.map((row) => [row.code, row.quantity]))
  return Object.entries(cart).map(([code, quantity]) => ({
    code,
    quantity,
    available: shown.get(code) ?? null,
    problem: problems[code] ?? null,
  }))
}

/** A half-typed quantity waits for the send; too many and no longer listed are already true while typing. */
export function shownProblem(problem: NeedProblem | null, attempted: boolean): NeedProblem | null {
  return problem === 'quantity' && !attempted ? null : problem
}

/** `REQUEST_VAT` in oreas_requests.py; `oreasRequestPrice.test.ts` turns red when the two part. */
export const REQUEST_VAT: VatType = '10%'
const NET_UNIT_LIMIT = 10n ** 15n

/** `net_unit_price` in oreas_requests.py: the whole-tugrik price shown, less the 10% it includes, half-up to 5 places. Null means not set, never 0. */
export function netUnitPrice(price: string | null | undefined): number | null {
  const found = /^(\d+)(?:\.(\d*))?$/.exec(price ?? '')
  if (!found) return null
  const shown = BigInt(found[1]) + ((found[2]?.[0] ?? '0') >= '5' ? 1n : 0n)
  const scaled = (2n * shown * 1_000_000n + 11n) / 22n
  return scaled < NET_UNIT_LIMIT ? Number(scaled) / 100_000 : null
}

export interface BreakdownLine extends CartLine {
  unitPrice: number | null
  amount: number | null
}

export interface RequestBreakdown {
  lines: BreakdownLine[]
  notCounted: string[]
  totals: Totals | null
}

/** The figures the quotation will hold; a line with no price, a problem or no listing is named as left out, never counted as 0. */
export function requestBreakdown(lines: readonly CartLine[], rows: readonly InStockRow[]): RequestBreakdown {
  const prices = new Map(rows.map((row) => [row.code, netUnitPrice(row.price_mnt)]))
  const counted: LineInput[] = []
  const notCounted: string[] = []
  const shown = lines.map((line): BreakdownLine => {
    const unitPrice = prices.get(line.code) ?? null
    if (unitPrice === null || line.problem) {
      notCounted.push(line.code)
      return { ...line, unitPrice, amount: null }
    }
    const qty = Number(line.quantity.trim())
    counted.push({ qty, unitPrice })
    return { ...line, unitPrice, amount: lineAmountOf(qty, unitPrice) }
  })
  return {
    lines: shown,
    notCounted,
    totals: counted.length > 0 ? computeTotals(counted, { salesType: 'Quotation', vatType: REQUEST_VAT }) : null,
  }
}

function customerPayload(form: CustomerForm) {
  return {
    name: form.name.trim(),
    company: form.company.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    note: form.note.trim() || null,
    website: form.website,
  }
}

export function needPayload(form: CustomerForm, cart: NeedCart) {
  return {
    kind: 'need' as const,
    ...customerPayload(form),
    registration_no: form.registration.trim() || null,
    address: form.address.trim() || null,
    lines: Object.entries(cart).map(([code, raw]) => ({ code, quantity: Number(raw.trim()) })),
  }
}

export function contactPayload(form: CustomerForm, code: string) {
  return { kind: 'contact' as const, ...customerPayload(form), code }
}

export type PublicRequestPayload = ReturnType<typeof needPayload> | ReturnType<typeof contactPayload>

export class PublicRequestError extends Error {
  readonly status: number
  readonly detail: string | null

  constructor(status: number, detail: string | null) {
    super(detail ?? `the request answered ${status}`)
    this.status = status
    this.detail = detail
  }
}

/** The server's sentence when it sent one; a field-by-field 422 list has none to show. */
export function publicRequestDetail(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return null
  return typeof body.detail === 'string' ? body.detail : null
}

export interface PublicRequestFailure {
  key: 'busy' | 'refused' | 'failed'
  status: number | string
  detail: string | null
}

/** What to tell the customer: the server's own sentence first, and a status when nothing better is known. */
export function publicRequestFailure(error: unknown): PublicRequestFailure {
  if (!(error instanceof PublicRequestError)) return { key: 'failed', status: '-', detail: null }
  if (error.status === 429) return { key: 'busy', status: 429, detail: null }
  if (error.detail) return { key: 'failed', status: error.status, detail: error.detail }
  return { key: error.status === 422 ? 'refused' : 'failed', status: error.status, detail: null }
}

export async function sendPublicRequest(payload: PublicRequestPayload): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ''}${REQUEST_PATH}`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (response.ok) return
  const body: unknown = await response.json().catch(() => null)
  throw new PublicRequestError(response.status, publicRequestDetail(body))
}

/** No client token and no credentials, so opening the public page can never start a sign-in. */
export async function fetchPublicStock(): Promise<PublicStock> {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ''}${FEED_PATH}`, { credentials: 'omit' })
  if (!response.ok) throw new Error(`stock list answered ${response.status}`)
  return (await response.json()) as PublicStock
}

export function usePublicStock() {
  return useQuery({
    queryKey: ['public', 'oreas'],
    queryFn: fetchPublicStock,
    refetchInterval: 60_000,
    retry: 1,
  })
}

export interface OreasRequestLine {
  id: string
  code: string
  quantity: number
  stock_qty: number
  has_price: boolean
  offer_stage: string | null
  reduced_by: number | null
  reduced_at_stage: string | null
  given_back: boolean
  give_back_offered: boolean
}

export interface OreasRequest {
  id: string
  kind: string
  status: string
  name: string
  company: string
  phone: string
  email: string
  registration_no: string | null
  address: string | null
  note: string | null
  product_code: string | null
  quotation_id: string | null
  quotation_no: string | null
  decided_by: string | null
  decided_at: string | null
  created_at: string
  lines: OreasRequestLine[]
}

export interface OreasClientChoice {
  id: string
  name: string
  client_no: string | null
  registration_no: string
  default_issuer_identity_id: string | null
}

export interface OreasIssuerChoice {
  id: string
  slug: string
  name_en: string
  name_mn: string
  name_ru: string
  is_default: boolean
}

export interface OreasStockChange {
  id: string
  old_qty: number
  new_qty: number
  source: string
  actor: string
  document_no: string | null
  created_at: string
}

/** The row written when a product is made names no change; every real change moves the number. */
export function stockChangeReason(change: Pick<OreasStockChange, 'old_qty' | 'new_qty' | 'source'>): string {
  return change.source === 'manual' && change.old_qty === change.new_qty ? 'created' : change.source
}

/** The server still refuses when the product has history; this only hides the button on an enabled one. */
export function canOfferDelete(product: Pick<OreasProduct, 'is_active'>): boolean {
  return !product.is_active
}

export function resolveFormFromRequest(request: OreasRequest): CustomerForm {
  return {
    ...NO_CUSTOMER,
    name: request.name,
    company: request.company,
    phone: request.phone,
    email: request.email,
    registration: request.registration_no ?? '',
    address: request.address ?? '',
  }
}

export function resolvePayload(form: CustomerForm, clientId: string, issuerId: string) {
  return {
    client_id: clientId || null,
    new_client: !clientId,
    issuer_identity_id: issuerId || null,
    name: form.name.trim(),
    company: form.company.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    registration_no: form.registration.trim() || null,
    address: form.address.trim() || null,
  }
}

/** The person's pick, else the picked client's own company, else the organization's default. */
export function chosenIssuer(
  pick: string,
  client: Pick<OreasClientChoice, 'default_issuer_identity_id'> | undefined,
  issuers: readonly Pick<OreasIssuerChoice, 'id' | 'is_default'>[],
): string {
  return pick || client?.default_issuer_identity_id || issuers.find((issuer) => issuer.is_default)?.id || ''
}

/** A whole number the server would take, or null. */
export function offerQuantity(text: string): number | null {
  if (!/^[0-9]+$/.test(text.trim())) return null
  const value = Number(text.trim())
  return value >= 1 && value <= QTY_MAX ? value : null
}

export function noPriceCodes(request: Pick<OreasRequest, 'lines'>): string[] {
  return request.lines.filter((line) => !line.has_price).map((line) => line.code)
}

/** Resolved with no link left means the staff deleted the Draft; a request that never made one is not this. */
export function isQuotationGone(request: Pick<OreasRequest, 'status' | 'quotation_id'>): boolean {
  return request.status === 'resolved' && request.quotation_id === null
}

/** The lines asking someone to take stock down or give it back, each with its request. */
export interface OreasAwaiting {
  count: number | null
}

export const OREAS_REQUESTS_LINK = '/inventory?view=oreas&section=requests'

/** The Requests button's number: new requests and waiting answers, or the panel holding the answers has no sign pointing at it. */
export function requestsSectionCount(newRequests: number, waiting: readonly OreasRequest[]): number {
  return newRequests + linesWaitingForAnswer(waiting).length
}

export function linesWaitingForAnswer(
  requests: readonly OreasRequest[],
): { request: OreasRequest; line: OreasRequestLine }[] {
  return requests.flatMap((request) =>
    request.lines.filter((line) => line.offer_stage || line.give_back_offered).map((line) => ({ request, line })),
  )
}
